import React, { useCallback, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { InfoWidgetType, SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedInfoWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { SMLSegmented, SegmentedOption } from '../controls/SMLSegmented';
import { blockSummaryMessages } from '../blocks/blockSummaryMessages';
import { BlockAutosave } from './useBlockAutosave';
import { formMessages } from './formMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

export const TEXT_MAX_LENGTH = 500;
/** The counter shows from this length (design d1). */
const COUNTER_FROM = 400;

interface TextBlockFormProps {
  widget: SerializedInfoWidget;
  autosave: BlockAutosave;
}

/** The Text block (design d0, d1): the style and the text. `{ text, type }`. */
const TextBlockFormComponent: React.FC<TextBlockFormProps> = ({ widget, autosave }) => {
  const intl = useIntl();
  const textId = useId();
  const [text, setText] = useState(widget.data.text);
  const [style, setStyle] = useState<InfoWidgetType>(widget.data.type);
  const { save, markInvalid } = autosave;

  const styleOptions = useMemo<SegmentedOption<InfoWidgetType>[]>(
    () => [
      { value: InfoWidgetType.title, label: intl.formatMessage(blockSummaryMessages.styleTitle) },
      { value: InfoWidgetType.normal, label: intl.formatMessage(blockSummaryMessages.styleNormal) },
      { value: InfoWidgetType.small, label: intl.formatMessage(blockSummaryMessages.styleSmall) },
    ],
    [intl]
  );

  const commit = useCallback(
    (nextText: string, nextStyle: InfoWidgetType) => {
      if (nextText.trim() === '' || nextText.length > TEXT_MAX_LENGTH) {
        markInvalid();
        return;
      }
      save({ text: nextText, type: nextStyle });
    },
    [markInvalid, save]
  );

  const onText = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setText(event.target.value);
      commit(event.target.value, style);
    },
    [commit, style]
  );
  const onStyle = useCallback(
    (next: InfoWidgetType) => {
      setStyle(next);
      commit(text, next);
    },
    [commit, text]
  );

  const isEmpty = text.trim() === '';
  const isTooLong = text.length > TEXT_MAX_LENGTH;
  let help = formMessages.textHelpNormal;
  if (style === InfoWidgetType.title) {
    help = formMessages.textHelpTitle;
  } else if (style === InfoWidgetType.small) {
    help = formMessages.textHelpSmall;
  }

  return (
    <div className={styles.form} data-testid={`form-${SmartLinkWidgetType.info}`}>
      <div className={styles.field}>
        <span className={styles.label}>{intl.formatMessage(formMessages.style)}</span>
        <SMLSegmented
          options={styleOptions}
          value={style}
          onChange={onStyle}
          ariaLabel={intl.formatMessage(formMessages.style)}
        />
      </div>
      <div className={styles.field}>
        <label htmlFor={textId} className={styles.label}>
          {intl.formatMessage(formMessages.text)}
        </label>
        <textarea
          id={textId}
          className={clsx(styles.input, styles.textarea, (isEmpty || isTooLong) && styles.inputBad)}
          value={text}
          onChange={onText}
          aria-invalid={isEmpty || isTooLong}
          data-testid="text-block-text"
        />
        {text.length >= COUNTER_FROM && (
          <span className={styles.counter}>
            {text.length} / {TEXT_MAX_LENGTH}
          </span>
        )}
        {isEmpty && <p className={styles.helpBad}>{intl.formatMessage(formMessages.textEmpty)}</p>}
        {isTooLong && (
          <p className={styles.helpBad}>{intl.formatMessage(formMessages.textTooLong, { max: TEXT_MAX_LENGTH })}</p>
        )}
        {!isEmpty && !isTooLong && <p className={styles.help}>{intl.formatMessage(help)}</p>}
      </div>
    </div>
  );
};

export const TextBlockForm = TypedMemo(TextBlockFormComponent);
